import {
  ComponentClass,
  FC,
  FunctionComponent,
  ReactNode,
  useContext,
} from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  RouteProps,
  withRouter,
} from "react-router-dom";

import { AppContext } from "contexts";
import { mountQueryString } from "lib/helpers";
import { routeEquals } from "routes/config";
import { User } from "services/users/types";

import * as routes from "../../routes";

type PermissionLevel =
  | "loginRequired"
  | "emailVerified"
  | "setupFinalized"
  | "anonymousOrSetupFinalized"
  | "sellerLoginRequired"
  | "registeredSeller"
  | "staffRequired";

interface ProtectedRoutesProps {
  protectBy: PermissionLevel;
}

interface RedirectRule {
  depends: PermissionLevel;
  redirectTo: string;
  rule: boolean;
  allowPageAccess?: string;
}

interface RedirectRules {
  loginRequired: RedirectRule;
  emailVerified: RedirectRule;
  setupFinalized: RedirectRule;
  anonymousOrSetupFinalized: RedirectRule;
  sellerLoginRequired: RedirectRule;
  registeredSeller: RedirectRule;
  staffRequired: RedirectRule;
}

type ProtectedRouteProps = RouteProps &
  ProtectedRoutesProps &
  RouteComponentProps;

type ProtectedElementType<T> = (
  element: T,
  user: User,
  rule: RedirectRule,
  currentRoute: string,
  queryString: string,
) => any;

const redirectionConfig = (user: User): RedirectRules => ({
  loginRequired: {
    depends: null,
    redirectTo: routes.directoryLogin(),
    rule: !!user,
  },
  emailVerified: {
    depends: "loginRequired",
    redirectTo: routes.directoryEmailVerificationSent(),
    rule: !!user && (user.isEmailVerified || user.isSeller),
  },
  setupFinalized: {
    depends: "emailVerified",
    redirectTo: routes.directoryFinalizeSetup(),
    rule: !!user && user.isSetupFinalized,
  },
  anonymousOrSetupFinalized: {
    depends: null,
    redirectTo: routes.directoryFinalizeSetup(),
    rule:
      !user ||
      (!!user && user.isSetupFinalized && user.isEmailVerified) ||
      user.isSeller,
  },
  sellerLoginRequired: {
    depends: null,
    redirectTo: routes.sellerCreateAccount(),
    rule: !!user,
  },
  registeredSeller: {
    depends: "sellerLoginRequired",
    redirectTo: routes.sellerAddRoyaltyAccount(),
    rule: !!user && user.canSeeSellerDashboard,
    allowPageAccess: routes.sellerAddRoyaltyAccount(),
  },
  staffRequired: {
    depends: null,
    redirectTo: routes.adminLogin(),
    rule: !!user && user.isStaff,
  },
});

const processRule: (
  user: User,
  rule: RedirectRule,
  currentRoute: string,
) => string = (user, rule, currentRoute) => {
  let redirect = null;
  if (rule.depends) {
    redirect = processRule(
      user,
      redirectionConfig(user)[rule.depends],
      currentRoute,
    );
  }
  if (!redirect && !rule.rule) {
    redirect = routeEquals(rule.allowPageAccess, currentRoute)
      ? null
      : rule.redirectTo;
  }
  return redirect;
};

const protectedElement: ProtectedElementType<any> = (
  element,
  user,
  rule,
  currentRoute,
  queryString,
) => {
  if (element === undefined) {
    return;
  }
  const redirectTo = processRule(user, rule, currentRoute);
  if (redirectTo) {
    return () => (
      <Redirect
        to={redirectTo + mountQueryString({ next: currentRoute + queryString })}
      ></Redirect>
    );
  }
  return element;
};

const protectedRender: ProtectedElementType<
  (props: RouteComponentProps<any>) => ReactNode
> = protectedElement;

const protectedComponent: ProtectedElementType<
  ComponentClass<any, any> | FunctionComponent<any>
> = protectedElement;

const ProtectedRoute: FC<ProtectedRouteProps> = ({
  path,
  render,
  protectBy,
  location,
  component,
  ...restProps
}) => {
  const { user } = useContext(AppContext);
  const rule = redirectionConfig(user)[protectBy];
  return (
    <Route
      path={path}
      render={protectedRender(
        render,
        user,
        rule,
        location.pathname,
        location.search,
      )}
      component={protectedComponent(
        component,
        user,
        rule,
        location.pathname,
        location.search,
      )}
      {...restProps}
    ></Route>
  );
};

export default withRouter(ProtectedRoute);
